<app-menu>
  <div
    class="qw-shell"
    [class.qw-shell--dark]="isDarkTheme"
    [class.qw-shell--light]="!isDarkTheme"
  >
    <header class="qw-hero">
      <div class="qw-hero-copy">
        <div class="qw-eyebrow">
          <span class="qw-live-dot"></span> Commercial operations
        </div>
        <div class="qw-title-row">
          <h1>Quote Workbench</h1>
          <span class="qw-count-pill">{{ total | number }} in queue</span>
        </div>
        <p>
          Assess customer-approved requests, compare charge and cost evidence,
          secure capacity, and release ready freight into operations.
        </p>
      </div>
      <div class="qw-hero-actions">
        <button
          type="button"
          class="qw-icon-button"
          (click)="toggleTheme()"
          [attr.aria-label]="isDarkTheme ? 'Use light theme' : 'Use dark theme'"
          [title]="isDarkTheme ? 'Use light theme' : 'Use dark theme'"
        >
          <i
            class="fa-solid"
            [ngClass]="isDarkTheme ? 'fa-sun' : 'fa-moon'"
          ></i>
        </button>
        <button
          type="button"
          class="qw-button"
          (click)="load(false)"
          [disabled]="loading"
        >
          <i
            class="fa-solid"
            [ngClass]="loading ? 'fa-circle-notch fa-spin' : 'fa-arrows-rotate'"
          ></i>
          Refresh
        </button>
        <button
          type="button"
          class="qw-button qw-button--primary"
          (click)="buildQuote()"
        >
          <i class="fa-solid fa-plus"></i>
          New quote
        </button>
      </div>
    </header>

    <main class="qw-main">
      <section class="qw-insights" aria-label="Page summary">
        <article class="qw-insight qw-insight--blue">
          <span class="qw-insight-icon"
            ><i class="fa-solid fa-layer-group"></i
          ></span>
          <div>
            <strong>{{ total | number }}</strong
            ><span>Total matching queue</span>
          </div>
        </article>
        <article class="qw-insight qw-insight--amber">
          <span class="qw-insight-icon"
            ><i class="fa-solid fa-clipboard-check"></i
          ></span>
          <div>
            <strong>{{ pageNeedsAssessment | number }}</strong
            ><span>Need assessment on page</span>
          </div>
        </article>
        <article class="qw-insight qw-insight--violet">
          <span class="qw-insight-icon"
            ><i class="fa-solid fa-truck-fast"></i
          ></span>
          <div>
            <strong>{{ pageNeedsCapacity | number }}</strong
            ><span>Need capacity on page</span>
          </div>
        </article>
        <article class="qw-insight qw-insight--green">
          <span class="qw-insight-icon"
            ><i class="fa-solid fa-circle-check"></i
          ></span>
          <div>
            <strong>{{ pageReadyToConvert | number }}</strong
            ><span>Ready to convert on page</span>
          </div>
        </article>
        <article class="qw-insight qw-insight--cyan">
          <span class="qw-insight-icon"
            ><i class="fa-solid fa-chart-line"></i
          ></span>
          <div>
            <strong>{{
              pageAverageMargin === null
                ? "—"
                : formatPercent(pageAverageMargin)
            }}</strong
            ><span>Average complete margin on page</span>
          </div>
        </article>
      </section>

      <section class="qw-filter-panel">
        <div class="qw-filter-primary">
          <label class="qw-field qw-field--search">
            <span>Search quote, customer, or lane</span>
            <div class="qw-input-icon">
              <i class="fa-solid fa-magnifying-glass"></i>
              <input
                type="search"
                [(ngModel)]="search"
                (keyup.enter)="applyFilters()"
                placeholder="Quote number, customer, origin, destination"
              />
            </div>
          </label>
          <label class="qw-field">
            <span>Quote status</span>
            <select [(ngModel)]="status" (change)="applyFilters()">
              <option *ngFor="let option of statusOptions" [value]="option">
                {{ option ? label(option) : "All statuses" }}
              </option>
            </select>
          </label>
          <label class="qw-field">
            <span>Assessment</span>
            <select [(ngModel)]="assessmentStatus" (change)="applyFilters()">
              <option
                *ngFor="let option of assessmentStatusOptions"
                [value]="option"
              >
                {{ option ? label(option) : "Any assessment" }}
              </option>
            </select>
          </label>
          <label class="qw-field">
            <span>Capacity</span>
            <select [(ngModel)]="capacityStatus" (change)="applyFilters()">
              <option
                *ngFor="let option of capacityStatusOptions"
                [value]="option"
              >
                {{ option ? label(option) : "Any capacity" }}
              </option>
            </select>
          </label>
          <label class="qw-field">
            <span>Server sort</span>
            <select [(ngModel)]="sort" (change)="applyFilters()">
              <option *ngFor="let option of sortOptions" [value]="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <div class="qw-filter-actions">
            <button
              type="button"
              class="qw-button qw-button--primary"
              (click)="applyFilters()"
              [disabled]="loading"
            >
              Apply
            </button>
            <button
              type="button"
              class="qw-button"
              (click)="toggleAdvancedFilters()"
              [class.is-active]="showAdvancedFilters"
            >
              <i class="fa-solid fa-sliders"></i>
              More
            </button>
          </div>
        </div>

        <div class="qw-filter-advanced" *ngIf="showAdvancedFilters">
          <label class="qw-field">
            <span>Source</span>
            <select [(ngModel)]="source">
              <option *ngFor="let option of sourceOptions" [value]="option">
                {{ option ? label(option) : "All sources" }}
              </option>
            </select>
          </label>
          <label class="qw-field">
            <span>Quote mode</span>
            <select [(ngModel)]="quoteMode">
              <option *ngFor="let option of modeOptions" [value]="option">
                {{ option ? label(option) : "All modes" }}
              </option>
            </select>
          </label>
          <label class="qw-field">
            <span>Customer state</span>
            <select [(ngModel)]="customerStatus">
              <option
                *ngFor="let option of customerStatusOptions"
                [value]="option"
              >
                {{ option ? label(option) : "Any customer state" }}
              </option>
            </select>
          </label>
          <label class="qw-field">
            <span>Priority</span>
            <select [(ngModel)]="priority">
              <option *ngFor="let option of priorityOptions" [value]="option">
                {{ option === "" ? "Any priority" : "Priority " + option }}
              </option>
            </select>
          </label>
          <label class="qw-field">
            <span>Pickup from</span>
            <input type="date" [(ngModel)]="pickupFrom" />
          </label>
          <label class="qw-field">
            <span>Pickup to</span>
            <input type="date" [(ngModel)]="pickupTo" />
          </label>
          <label class="qw-field">
            <span>Conversion</span>
            <select [(ngModel)]="converted">
              <option value="">Any conversion state</option>
              <option value="false">Not converted</option>
              <option value="true">Converted</option>
            </select>
          </label>
          <label class="qw-check"
            ><input type="checkbox" [(ngModel)]="onlyUnassigned" /><span
              >Unassigned only</span
            ></label
          >
          <label class="qw-check"
            ><input type="checkbox" [(ngModel)]="onlyBlockers" /><span
              >Has blockers</span
            ></label
          >
          <div class="qw-filter-advanced-actions">
            <button
              type="button"
              class="qw-button qw-button--primary"
              (click)="applyFilters()"
              [disabled]="loading"
            >
              Apply filters
            </button>
            <button
              type="button"
              class="qw-button"
              (click)="resetFilters()"
              [disabled]="loading || !hasActiveFilters"
            >
              Reset all
            </button>
          </div>
        </div>

        <div
          class="qw-filter-chips"
          *ngIf="hasActiveFilters && !showAdvancedFilters"
        >
          <span><i class="fa-solid fa-filter"></i> Filtered queue</span>
          <button type="button" (click)="resetFilters()">Clear all</button>
        </div>
      </section>

      <div class="qw-alert qw-alert--error" *ngIf="errorMessage" role="alert">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <div>
          <strong>Quote queue unavailable</strong
          ><span>{{ errorMessage }}</span>
        </div>
        <button type="button" class="qw-button" (click)="load(false)">
          Retry
        </button>
      </div>

      <section class="qw-grid-card">
        <header class="qw-grid-header">
          <div>
            <h2>Active quote queue</h2>
            <p>
              Customer charge, carrier cost, margin, readiness, and capacity in
              one operational view.
            </p>
          </div>
          <div class="qw-grid-legend">
            <span><i class="qw-dot qw-dot--amber"></i> Assessment</span>
            <span><i class="qw-dot qw-dot--violet"></i> Capacity</span>
            <span><i class="qw-dot qw-dot--red"></i> Blocker</span>
          </div>
        </header>

        <div class="qw-grid-shell">
          <ag-grid-angular
            class="qw-grid"
            [ngClass]="gridThemeClass"
            [rowData]="rows"
            [columnDefs]="columnDefs"
            [defaultColDef]="defaultColDef"
            [gridOptions]="gridOptions"
            [overlayLoadingTemplate]="overlayLoadingTemplate"
            [overlayNoRowsTemplate]="overlayNoRowsTemplate"
            (gridReady)="onGridReady($event)"
            (rowClicked)="onRowClicked($event)"
          >
          </ag-grid-angular>
        </div>

        <footer class="qw-grid-footer">
          <div class="qw-page-copy">
            <strong>{{ pageStart | number }}–{{ pageEnd | number }}</strong>
            <span>of {{ total | number }} matching quotes</span>
          </div>
          <div class="qw-page-actions">
            <label
              ><span>Rows</span
              ><select [(ngModel)]="pageSize" (change)="pageSizeChanged()">
                <option *ngFor="let size of pageSizeOptions" [ngValue]="size">
                  {{ size }}
                </option>
              </select></label
            >
            <button
              type="button"
              class="qw-icon-button"
              (click)="previousPage()"
              [disabled]="!canGoBack || loading"
              aria-label="Previous page"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              type="button"
              class="qw-icon-button"
              (click)="nextPage()"
              [disabled]="!hasMore || loading"
              aria-label="Next page"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </footer>
      </section>
    </main>

    <aside
      class="qw-drawer-backdrop"
      *ngIf="selectedSummary || detailLoading"
      (click)="closeDetail()"
    >
      <section
        class="qw-drawer"
        (click)="$event.stopPropagation()"
        role="dialog"
        aria-modal="true"
        aria-label="Quote assessment detail"
      >
        <header class="qw-drawer-header">
          <div class="qw-drawer-title">
            <span>Quote assessment</span>
            <div>
              <h2>
                {{ selectedSummary?.display_quote_number || "Loading quote" }}
              </h2>
              <em [class]="statusClass(selectedSummary?.effective_status)">{{
                label(selectedSummary?.effective_status)
              }}</em>
            </div>
            <p>
              {{ selectedSummary?.customer_display_name || "Customer pending" }}
              · {{ selectedSummary?.origin_display || "Origin pending" }} →
              {{
                selectedSummary?.destination_display || "Destination pending"
              }}
            </p>
          </div>
          <button type="button" class="qw-button qw-button--primary" *ngIf="selectedDetail && selectedDetail.rate_response['customer_quote_pdf']" (click)="downloadSelectedPdf()" [disabled]="pdfDownloading">
            <i class="fa-solid" [ngClass]="pdfDownloading ? 'fa-circle-notch fa-spin' : 'fa-file-arrow-down'"></i>
            {{ pdfDownloading ? 'Downloading' : 'Download PDF' }}
          </button>
          <button
            type="button"
            class="qw-icon-button"
            (click)="closeDetail()"
            aria-label="Close quote detail"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div class="qw-drawer-body">
          <div class="qw-alert" role="alert" *ngIf="pdfError">{{ pdfError }}</div>
          <div class="qw-detail-loading" *ngIf="detailLoading">
            <i class="fa-solid fa-circle-notch fa-spin"></i>
            <strong>Loading pricing, freight, and market evidence</strong>
          </div>

          <div
            class="qw-alert qw-alert--error"
            *ngIf="!detailLoading && detailError"
            role="alert"
          >
            <i class="fa-solid fa-triangle-exclamation"></i>
            <div>
              <strong>Detail unavailable</strong><span>{{ detailError }}</span>
            </div>
            <button type="button" class="qw-button" (click)="retryDetail()">
              Retry
            </button>
          </div>

          <ng-container *ngIf="!detailLoading && selectedDetail as detail">
            <section class="qw-detail-money">
              <article>
                <span>Customer charge</span
                ><strong>{{
                  formatMoney(
                    detail.pricing.charge_total_cents,
                    detail.pricing.currency || detail.currency
                  )
                }}</strong
                ><small>{{
                  detail.pricing.charge_complete
                    ? "Complete"
                    : "Pricing incomplete"
                }}</small>
              </article>
              <article>
                <span>Carrier cost</span
                ><strong>{{
                  formatMoney(
                    detail.pricing.cost_total_cents,
                    detail.pricing.cost_currency
                  )
                }}</strong
                ><small>{{
                  detail.pricing.cost_complete ? "Complete" : "Cost incomplete"
                }}</small>
              </article>
              <article
                [class.is-negative]="
                  (detail.pricing.margin_amount_cents || 0) < 0
                "
                [class.is-warning]="detail.pricing.currency_mismatch"
              >
                <span>Margin</span
                ><strong>{{
                  detail.pricing.currency_mismatch
                    ? "Unavailable"
                    : formatMoney(
                        detail.pricing.margin_amount_cents,
                        detail.pricing.currency
                      )
                }}</strong
                ><small>{{
                  detail.pricing.currency_mismatch
                    ? "Currency mismatch"
                    : formatPercent(detail.pricing.margin_percent)
                }}</small>
              </article>
              <article>
                <span>Current pricing</span
                ><strong
                  >v{{
                    detail.pricing.version_number === null
                      ? "—"
                      : detail.pricing.version_number
                  }}</strong
                ><small
                  >{{ label(detail.pricing.version_status) }} ·
                  {{ label(detail.pricing.pricing_source) }}</small
                >
              </article>
            </section>

            <section class="qw-readiness-panel">
              <div class="qw-section-heading">
                <div>
                  <span>Release readiness</span>
                  <h3>Quote → lading/work order</h3>
                </div>
                <strong
                  [class.is-ready]="detail.capabilities.can_convert_to_lading"
                  >{{
                    detail.capabilities.can_convert_to_lading
                      ? "Ready"
                      : "Not ready"
                  }}</strong
                >
              </div>
              <div class="qw-readiness-track">
                <article
                  [class.is-complete]="detail.customer_status === 'approved'"
                >
                  <i class="fa-solid fa-user-check"></i><span>Customer</span
                  ><strong>{{ label(detail.customer_status) }}</strong>
                </article>
                <article
                  [class.is-complete]="detail.assessment_status === 'approved'"
                >
                  <i class="fa-solid fa-clipboard-check"></i
                  ><span>Assessment</span
                  ><strong>{{ label(detail.assessment_status) }}</strong>
                </article>
                <article [class.is-complete]="detail.pricing.margin_complete">
                  <i class="fa-solid fa-calculator"></i><span>Financials</span
                  ><strong>{{
                    detail.pricing.margin_complete ? "Complete" : "Incomplete"
                  }}</strong>
                </article>
                <article
                  [class.is-complete]="
                    detail.capacity_status === 'selected' ||
                    detail.capacity_status === 'confirmed'
                  "
                >
                  <i class="fa-solid fa-truck"></i><span>Capacity</span
                  ><strong>{{ label(detail.capacity_status) }}</strong>
                </article>
              </div>
            </section>

            <ng-container
              *ngIf="detail.workbench.current_pricing_version as pricing"
            >
              <section
                class="qw-issue-panel qw-issue-panel--blocker"
                *ngIf="issueList(pricing.blocking_errors).length"
              >
                <div>
                  <i class="fa-solid fa-octagon-exclamation"></i
                  ><strong>Blocking issues</strong>
                </div>
                <p
                  *ngFor="
                    let issue of issueList(pricing.blocking_errors);
                    trackBy: trackByIndex
                  "
                >
                  {{ issue }}
                </p>
              </section>
              <section
                class="qw-issue-panel qw-issue-panel--warning"
                *ngIf="issueList(pricing.warnings).length"
              >
                <div>
                  <i class="fa-solid fa-triangle-exclamation"></i
                  ><strong>Pricing warnings</strong>
                </div>
                <p
                  *ngFor="
                    let issue of issueList(pricing.warnings);
                    trackBy: trackByIndex
                  "
                >
                  {{ issue }}
                </p>
              </section>
            </ng-container>

            <section class="qw-detail-section">
              <div class="qw-section-heading">
                <div>
                  <span>Shipment</span>
                  <h3>Lane and freight</h3>
                </div>
                <small>{{
                  detail.freight.rated_miles
                    ? (detail.freight.rated_miles | number) + " rated mi"
                    : "Mileage pending"
                }}</small>
              </div>
              <div class="qw-lane-card">
                <article>
                  <i class="fa-solid fa-circle-dot"></i>
                  <div>
                    <span>Origin</span
                    ><strong>{{
                      detail.origin_display || "Origin pending"
                    }}</strong>
                  </div>
                </article>
                <div class="qw-lane-line">
                  <span></span><i class="fa-solid fa-truck-fast"></i
                  ><span></span>
                </div>
                <article>
                  <i class="fa-solid fa-location-dot"></i>
                  <div>
                    <span>Destination</span
                    ><strong>{{
                      detail.destination_display || "Destination pending"
                    }}</strong>
                  </div>
                </article>
              </div>
              <div class="qw-fact-grid">
                <article>
                  <span>Pickup</span
                  ><strong>{{
                    formatDate(detail.freight.requested_pickup_date)
                  }}</strong>
                </article>
                <article>
                  <span>Delivery</span
                  ><strong>{{
                    formatDate(detail.freight.requested_delivery_date)
                  }}</strong>
                </article>
                <article>
                  <span>Equipment</span
                  ><strong>{{
                    detail.freight.equipment_type || "Not set"
                  }}</strong>
                </article>
                <article>
                  <span>Load type</span
                  ><strong>{{
                    label(detail.freight.freight_load_type)
                  }}</strong>
                </article>
                <article>
                  <span>Weight</span
                  ><strong>{{
                    detail.freight.weight === null ||
                    detail.freight.weight === undefined
                      ? "Not set"
                      : (detail.freight.weight | number) +
                        " " +
                        (detail.freight.weight_unit || "")
                  }}</strong>
                </article>
                <article>
                  <span>Handling</span
                  ><strong
                    >{{ formatCount(detail.freight.pallet_count, "pallet") }} ·
                    {{
                      formatCount(detail.freight.piece_count, "piece")
                    }}</strong
                  >
                </article>
              </div>
            </section>

            <section class="qw-detail-section">
              <div class="qw-section-heading">
                <div>
                  <span>Capacity</span>
                  <h3>Carrier options and going costs</h3>
                </div>
                <small
                  >{{ carrierOptions(detail).length }} options ·
                  {{ carrierCosts(detail).length }} cost records</small
                >
              </div>
              <div
                class="qw-option-list"
                *ngIf="carrierOptions(detail).length; else noCarrierOptions"
              >
                <article
                  *ngFor="
                    let option of carrierOptions(detail);
                    trackBy: trackByUuid
                  "
                  [class.is-selected]="
                    option.option_status === 'selected' ||
                    option.option_status === 'confirmed'
                  "
                >
                  <div class="qw-option-rank">
                    {{
                      option.rank_order === null ||
                      option.rank_order === undefined
                        ? "—"
                        : option.rank_order
                    }}
                  </div>
                  <div class="qw-option-name">
                    <strong>{{
                      option.carrier_name || "Unnamed carrier"
                    }}</strong
                    ><span
                      >{{ label(option.availability_status) }} ·
                      {{
                        formatCount(option.transit_days, "transit day")
                      }}</span
                    >
                  </div>
                  <div class="qw-score">
                    <span>Fit</span
                    ><strong>{{
                      option.suitability_score === null ||
                      option.suitability_score === undefined
                        ? "—"
                        : (option.suitability_score | number: "1.0-1")
                    }}</strong>
                  </div>
                  <div class="qw-score">
                    <span>Margin</span
                    ><strong>{{
                      option.margin_score === null ||
                      option.margin_score === undefined
                        ? "—"
                        : (option.margin_score | number: "1.0-1")
                    }}</strong>
                  </div>
                  <div class="qw-option-cost">
                    <strong>{{
                      formatMoney(option.cost_total_cents, option.cost_currency)
                    }}</strong
                    ><span>{{ label(option.option_status) }}</span>
                  </div>
                </article>
              </div>
              <ng-template #noCarrierOptions
                ><div class="qw-empty-section">
                  <i class="fa-solid fa-truck-ramp-box"></i
                  ><span
                    >No carrier options have been recorded for this pricing
                    version.</span
                  >
                </div></ng-template
              >

              <div class="qw-record-list" *ngIf="carrierCosts(detail).length">
                <article
                  *ngFor="
                    let header of carrierCosts(detail);
                    trackBy: trackByUuid
                  "
                >
                  <header>
                    <div>
                      <strong>{{
                        header.carrier_name || "Carrier cost"
                      }}</strong
                      ><span
                        >{{ label(header.cost_status) }} ·
                        {{ label(header.cost_kind) }}</span
                      >
                    </div>
                    <strong>{{
                      formatMoney(header.total_cents, header.currency_type)
                    }}</strong>
                  </header>
                  <div
                    class="qw-line-item"
                    *ngFor="let line of lineItems(header); trackBy: trackByUuid"
                  >
                    <div>
                      <strong>{{
                        line.service_name || line.service_type || "Service"
                      }}</strong
                      ><span
                        >{{ line.rate_unit || line.charge_mode || "rate" }} ×
                        {{
                          line.multiplier === null ||
                          line.multiplier === undefined
                            ? 1
                            : line.multiplier
                        }}</span
                      >
                    </div>
                    <strong>{{
                      formatMoney(line.net_amount_cents, header.currency_type)
                    }}</strong>
                  </div>
                  <p *ngIf="!lineItems(header).length">
                    No cost line items recorded.
                  </p>
                </article>
              </div>
            </section>

            <section class="qw-detail-section">
              <div class="qw-section-heading">
                <div>
                  <span>Customer</span>
                  <h3>Charge construction</h3>
                </div>
                <small
                  >{{ customerCharges(detail).length }} charge records</small
                >
              </div>
              <div
                class="qw-record-list"
                *ngIf="customerCharges(detail).length; else noCharges"
              >
                <article
                  *ngFor="
                    let header of customerCharges(detail);
                    trackBy: trackByUuid
                  "
                >
                  <header>
                    <div>
                      <strong>{{
                        detail.customer_display_name || "Customer charge"
                      }}</strong
                      ><span>{{ label(header.charge_status) }}</span>
                    </div>
                    <strong>{{
                      formatMoney(header.total_cents, header.currency_type)
                    }}</strong>
                  </header>
                  <div
                    class="qw-line-item"
                    *ngFor="let line of lineItems(header); trackBy: trackByUuid"
                  >
                    <div>
                      <strong>{{
                        line.service_name || line.service_type || "Service"
                      }}</strong
                      ><span
                        >{{ line.rate_unit || line.charge_mode || "rate" }} ×
                        {{
                          line.multiplier === null ||
                          line.multiplier === undefined
                            ? 1
                            : line.multiplier
                        }}</span
                      >
                    </div>
                    <strong>{{
                      formatMoney(line.net_amount_cents, header.currency_type)
                    }}</strong>
                  </div>
                  <p *ngIf="!lineItems(header).length">
                    No charge line items recorded.
                  </p>
                </article>
              </div>
              <ng-template #noCharges
                ><div class="qw-empty-section">
                  <i class="fa-solid fa-file-invoice-dollar"></i
                  ><span
                    >No customer charge has been recorded for this pricing
                    version.</span
                  >
                </div></ng-template
              >
            </section>

            <section class="qw-detail-section">
              <div class="qw-section-heading">
                <div>
                  <span>Pricing evidence</span>
                  <h3>Market observations and historical lanes</h3>
                </div>
                <small>Decision support only</small>
              </div>
              <div class="qw-evidence-grid">
                <div>
                  <h4>
                    Going rates
                    <span>{{ rateObservations(detail).length }}</span>
                  </h4>
                  <article
                    class="qw-evidence-row"
                    *ngFor="
                      let observation of rateObservations(detail);
                      trackBy: trackByUuid
                    "
                  >
                    <div>
                      <strong
                        >{{ label(observation.observation_side) }} ·
                        {{
                          label(
                            observation.source_provider ||
                              observation.source_kind
                          )
                        }}</strong
                      ><span
                        >{{ formatCount(observation.sample_count, "sample") }} ·
                        confidence
                        {{
                          observation.confidence_score === null ||
                          observation.confidence_score === undefined
                            ? "—"
                            : (observation.confidence_score | number: "1.0-2")
                        }}</span
                      >
                    </div>
                    <div>
                      <strong>{{
                        formatMoney(
                          observation.amount_cents,
                          observation.currency_type
                        )
                      }}</strong
                      ><span
                        *ngIf="
                          observation.low_amount_cents !== null ||
                          observation.high_amount_cents !== null
                        "
                        >{{
                          formatMoney(
                            observation.low_amount_cents,
                            observation.currency_type
                          )
                        }}–{{
                          formatMoney(
                            observation.high_amount_cents,
                            observation.currency_type
                          )
                        }}</span
                      >
                    </div>
                  </article>
                  <div
                    class="qw-empty-section qw-empty-section--small"
                    *ngIf="!rateObservations(detail).length"
                  >
                    No current market observations.
                  </div>
                </div>
                <div>
                  <h4>
                    Historical matches
                    <span>{{ historicalMatches(detail).length }}</span>
                  </h4>
                  <article
                    class="qw-evidence-row"
                    *ngFor="
                      let match of historicalMatches(detail);
                      trackBy: trackByUuid
                    "
                  >
                    <div>
                      <strong
                        >{{ label(match.match_tier) }} match ·
                        {{ formatDate(match.shipment_date) }}</strong
                      ><span
                        >Score
                        {{
                          match.match_score === null ||
                          match.match_score === undefined
                            ? "—"
                            : (match.match_score | number: "1.0-2")
                        }}</span
                      >
                    </div>
                    <div>
                      <strong
                        >{{
                          formatMoney(
                            match.historical_charge_cents,
                            match.currency_type
                          )
                        }}
                        charge</strong
                      ><span
                        >{{
                          formatMoney(
                            match.historical_cost_cents,
                            match.currency_type
                          )
                        }}
                        cost ·
                        {{
                          formatPercent(match.historical_margin_percent)
                        }}</span
                      >
                    </div>
                  </article>
                  <div
                    class="qw-empty-section qw-empty-section--small"
                    *ngIf="!historicalMatches(detail).length"
                  >
                    No comparable settled freight yet.
                  </div>
                </div>
              </div>
            </section>

            <section class="qw-detail-section">
              <div class="qw-section-heading">
                <div>
                  <span>Audit trail</span>
                  <h3>Pricing versions, approvals, and events</h3>
                </div>
                <small>Immutable evidence</small>
              </div>
              <div class="qw-audit-columns">
                <div>
                  <h4>Pricing versions</h4>
                  <article
                    class="qw-audit-row"
                    *ngFor="
                      let version of detail.workbench.pricing_versions;
                      trackBy: trackByUuid
                    "
                  >
                    <i
                      class="fa-solid"
                      [ngClass]="
                        version.is_current
                          ? 'fa-circle-check'
                          : 'fa-clock-rotate-left'
                      "
                    ></i>
                    <div>
                      <strong
                        >Version {{ version.version_number }} ·
                        {{ label(version.version_status) }}</strong
                      ><span
                        >{{ label(version.pricing_source) }} ·
                        {{ formatDate(version.created_at, true) }}</span
                      >
                    </div>
                    <em>{{
                      formatMoney(version.margin_amount_cents, version.currency)
                    }}</em>
                  </article>
                  <div
                    class="qw-empty-section qw-empty-section--small"
                    *ngIf="!detail.workbench.pricing_versions.length"
                  >
                    No pricing versions.
                  </div>
                </div>
                <div>
                  <h4>Acceptances</h4>
                  <article
                    class="qw-audit-row"
                    *ngFor="
                      let acceptance of acceptances(detail);
                      trackBy: trackByUuid
                    "
                  >
                    <i class="fa-solid fa-handshake"></i>
                    <div>
                      <strong>{{ label(acceptance.acceptance_type) }}</strong
                      ><span
                        >{{
                          acceptance.accepted_by ||
                            label(acceptance.acceptance_method)
                        }}
                        · {{ formatDate(acceptance.accepted_at, true) }}</span
                      >
                    </div>
                  </article>
                  <div
                    class="qw-empty-section qw-empty-section--small"
                    *ngIf="!acceptances(detail).length"
                  >
                    No acceptance evidence.
                  </div>
                </div>
              </div>
              <div class="qw-timeline" *ngIf="quoteEvents(detail).length">
                <article
                  *ngFor="
                    let event of quoteEvents(detail);
                    trackBy: trackByUuid
                  "
                >
                  <i></i>
                  <div>
                    <strong>{{ label(event.event_type) }}</strong
                    ><span
                      >{{ event.actor_name || label(event.actor_type) }} ·
                      {{ formatDate(event.occurred_at, true) }}</span
                    >
                  </div>
                </article>
              </div>
            </section>
          </ng-container>
        </div>

        <footer class="qw-drawer-footer" *ngIf="selectedSummary as quote">
          <div
            class="qw-conversion-message qw-conversion-message--error"
            *ngIf="conversionError"
          >
            <i class="fa-solid fa-triangle-exclamation"></i
            ><span>{{ conversionError }}</span>
          </div>
          <div
            class="qw-conversion-message qw-conversion-message--success"
            *ngIf="conversionResult"
          >
            <i class="fa-solid fa-circle-check"></i
            ><span
              >Work order {{ conversionResult.lading_id }} created on manifest
              {{ conversionResult.manifest_id }}.</span
            ><button type="button" (click)="openConvertedWorkOrder()">
              Open
            </button>
          </div>
          <div class="qw-drawer-footer-row">
            <div>
              <strong>{{ conversionReadinessLabel(quote) }}</strong
              ><span *ngIf="quote.converted_lading_uuid"
                >Linked lading: {{ quote.converted_lading_uuid }}</span
              >
            </div>
            <button
              type="button"
              class="qw-button"
              *ngIf="quote.converted_lading_uuid"
              (click)="openConvertedWorkOrder()"
            >
              <i class="fa-solid fa-arrow-up-right-from-square"></i> Open work
              order
            </button>
            <button
              type="button"
              class="qw-button qw-button--primary"
              *ngIf="!quote.converted_lading_uuid"
              (click)="convertSelected()"
              [disabled]="
                !quote.capabilities.can_convert_to_lading ||
                convertingQuoteUuid !== null
              "
            >
              <i
                class="fa-solid"
                [ngClass]="
                  convertingQuoteUuid
                    ? 'fa-circle-notch fa-spin'
                    : 'fa-arrow-right-to-bracket'
                "
              ></i>
              {{
                convertingQuoteUuid
                  ? "Creating work order…"
                  : "Convert to lading"
              }}
            </button>
          </div>
        </footer>
      </section>
    </aside>
  </div>
</app-menu>
